/**
 * 走行距離・所要時間の計算サービス
 *
 * 3層キャッシング戦略:
 * 1. forceRefresh=true: API呼び出し → 結果をキャッシュ
 * 2. キャッシュHIT: キャッシュデータを返す
 * 3. キャッシュMISS: API呼び出し → 結果をキャッシュ
 * 4. API失敗: 期限切れキャッシュをフォールバック
 */

import { DistanceCache, CachedDistanceData } from './distanceCacheService';
import { googleMapsService } from './googleMapsService';

// =============================================================================
// 型定義
// =============================================================================

export type DistanceResultSource = 'cache' | 'api' | 'expired-cache';

export interface DistanceResult {
  distance: number; // メートル
  duration: number; // 秒
  source: DistanceResultSource; // データソース
  cachedAt: string; // ISO 8601形式のキャッシュ時刻
  fromAddress: string;
  toAddress: string;
  accessedAt: string; // 最後にアクセスされた時刻
}

export interface DistanceCalculationOptions {
  forceRefresh?: boolean; // trueの場合、キャッシュをスキップしてAPI呼び出し
  timeout?: number; // API呼び出しのタイムアウト（ミリ秒）
}

// =============================================================================
// DistanceCalculationService クラス
// =============================================================================

export class DistanceCalculationService {
  private cache: DistanceCache;
  private readonly DEFAULT_TIMEOUT = 10000; // 10秒

  constructor(cache: DistanceCache) {
    this.cache = cache;
  }

  /**
   * 走行距離と所要時間を計算
   *
   * 3層キャッシング戦略:
   * 1. forceRefresh=true の場合: API呼び出し → キャッシュに保存
   * 2. キャッシュHIT: キャッシュデータを返す
   * 3. キャッシュMISS: API呼び出し → キャッシュに保存
   * 4. API失敗時: 期限切れキャッシュをフォールバック
   *
   * @param from - 出発地住所
   * @param to - 到着地住所
   * @param options - オプション（forceRefresh, timeout）
   * @returns 距離・時間データとソース情報
   */
  public async calculate(
    from: string,
    to: string,
    options: DistanceCalculationOptions = {}
  ): Promise<DistanceResult | null> {
    try {
      // Layer 1: forceRefreshが指定されている場合、キャッシュをスキップ
      if (!options.forceRefresh) {
        // Layer 2: キャッシュをチェック
        const cachedData = this.cache.get(from, to);
        if (cachedData) {
          return this.formatResult(cachedData, 'cache');
        }
      }

      // Layer 3: キャッシュミス → API呼び出し
      const apiResult = await this.callGoogleMapsAPI(from, to, options.timeout);
      if (apiResult) {
        // 結果をキャッシュに保存
        this.cache.set(from, to, apiResult.distance, apiResult.duration);
        return this.formatResult(
          {
            distance: apiResult.distance,
            duration: apiResult.duration,
            fromAddress: from,
            toAddress: to,
            cachedAt: new Date().toISOString(),
            accessedAt: new Date().toISOString(),
          },
          'api'
        );
      }

      // Layer 4: API失敗 → 期限切れキャッシュをフォールバック
      const expiredCached = this.getExpiredCache(from, to);
      if (expiredCached) {
        return this.formatResult(expiredCached, 'expired-cache');
      }

      // すべての試行が失敗
      console.warn('Distance calculation failed for all sources', {
        from,
        to,
      });
      return null;
    } catch (error) {
      console.error('Distance calculation error:', error);
      // エラー時は期限切れキャッシュをフォールバック
      const expiredCached = this.getExpiredCache(from, to);
      if (expiredCached) {
        return this.formatResult(expiredCached, 'expired-cache');
      }
      return null;
    }
  }

  /**
   * Google Maps Directions APIを呼び出し
   * DirectionResultから距離・時間を抽出して返す
   */
  private async callGoogleMapsAPI(
    from: string,
    to: string,
    timeout?: number
  ): Promise<{ distance: number; duration: number } | null> {
    try {
      const timeoutMs = timeout || this.DEFAULT_TIMEOUT;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const result = await Promise.race([
        googleMapsService.getDirections(from, to),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('API call timeout')), timeoutMs + 100)
        ),
      ]);

      clearTimeout(timeoutId);

      // DirectionResult から距離・時間を抽出
      if (result && result.status === 'OK' && result.routes.length > 0) {
        const route = result.routes[0];
        let totalDistance = 0;
        let totalDuration = 0;

        // 全ての leg (区間) の距離と時間を合計
        route.legs.forEach(leg => {
          totalDistance += leg.distance?.value || 0;
          totalDuration += leg.duration?.value || 0;
        });

        return {
          distance: totalDistance, // メートル
          duration: totalDuration, // 秒
        };
      }

      return null;
    } catch (error) {
      console.warn('Google Maps API call failed:', error);
      return null;
    }
  }

  /**
   * LocalStorage内の期限切れキャッシュを取得
   * （APIが失敗した時のフォールバック用）
   */
  private getExpiredCache(from: string, to: string): CachedDistanceData | null {
    try {
      // 正規化したアドレスで検索
      const normalizedFrom = this.normalizeAddress(from);
      const normalizedTo = this.normalizeAddress(to);

      // LocalStorageを直接検索してフォールバック用キャッシュを取得
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith('rakuhai_distance_cache_')) {
          continue;
        }

        // キーに正規化されたアドレスが含まれているか確認
        if (key.includes(normalizedFrom) && key.includes(normalizedTo)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const data: CachedDistanceData = JSON.parse(cached);
              // キャッシュの日付は関係なく返す（期限切れでも使用）
              return data;
            } catch {
              // パース失敗時はスキップ
              continue;
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.warn('Failed to get expired cache:', error);
      return null;
    }
  }

  /**
   * 住所を正規化（DistanceCacheと同じロジック）
   */
  private normalizeAddress(address: string): string {
    return address.toLowerCase().replace(/[\s-]/g, '').substring(0, 50);
  }

  /**
   * CachedDistanceDataをDistanceResultに変換
   */
  private formatResult(cached: CachedDistanceData, source: DistanceResultSource): DistanceResult {
    return {
      distance: cached.distance,
      duration: cached.duration,
      source,
      cachedAt: cached.cachedAt,
      fromAddress: cached.fromAddress,
      toAddress: cached.toAddress,
      accessedAt: cached.accessedAt,
    };
  }

  /**
   * キャッシュ統計情報を取得（デバッグ用）
   */
  public getStats() {
    return this.cache.getStats();
  }

  /**
   * キャッシュをクリア
   */
  public clearCache() {
    this.cache.clear();
  }
}

// =============================================================================
// エクスポート用シングルトンインスタンス
// =============================================================================

import { distanceCache } from './distanceCacheService';

export const distanceCalculationService = new DistanceCalculationService(distanceCache);

export default DistanceCalculationService;
